// /support-inbox — support request inbox with claim/resolve actions (Wave 4)
// Spec: docs/plans/admin-tooling.md D-62

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StepUpModal } from '@/components/admin/step-up-modal';
import { StatusPill } from '@/components/admin/status-pill';

// ── Types ─────────────────────────────────────────────────────────────────────

type SupportStatus = 'pending' | 'claimed' | 'resolved';

interface SupportRow {
  request_id: string; // 32-char hex
  request_type: string;
  claimed_login_name: string | null;
  contact_email: string;
  narrative: string;
  requester_ip: string;
  status: SupportStatus;
  claimed_by_staff_id: number | null;
  claimed_at: number | null;
  resolved_at: number | null;
  resolved_action: string | null;
  resolved_notes: string | null;
  created_at: number;
}

interface SupportListResponse {
  rows: SupportRow[];
  next_cursor: string | null;
}

// ── SupportActionModal (self-contained) ───────────────────────────────────────
// State machine: stepup → confirm → submitting → done | error

type ActionKind = 'claim' | 'resolve';
type ActionPhase = 'stepup' | 'confirm' | 'submitting' | 'done' | 'error';

interface SupportActionModalProps {
  requestId: string;
  kind: ActionKind;
  onDone: () => void;
  onCancel: () => void;
}

type ResolveOutcome = 'resolved' | 'rejected';

function ModalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-sm mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        {children}
      </div>
    </div>
  );
}

function SupportActionModal({ requestId, kind, onDone, onCancel }: SupportActionModalProps) {
  const [phase, setPhase] = useState<ActionPhase>('stepup');
  const [stepupToken, setStepupToken] = useState('');
  const [outcome, setOutcome] = useState<ResolveOutcome>('resolved');
  const [notes, setNotes] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  function handleVerified(token: string) {
    setStepupToken(token);
    setPhase('confirm');
  }

  function handleConfirm() {
    if (kind === 'resolve' && !notes.trim()) return;
    setPhase('submitting');

    void (async () => {
      let res;
      if (kind === 'claim') {
        res = await apiFetch(`/admin/support-requests/${requestId}/claim`, {
          method: 'POST',
          headers: { 'X-Stepup-Token': stepupToken },
          body: JSON.stringify({}),
        });
      } else {
        res = await apiFetch(`/admin/support-requests/${requestId}/resolve`, {
          method: 'POST',
          headers: { 'X-Stepup-Token': stepupToken },
          body: JSON.stringify({ outcome, notes: notes.trim() }),
        });
      }

      if (!res.ok) {
        const data = res.data as Record<string, unknown>;
        setErrorMsg((data['error'] as string | undefined) ?? 'Action failed. Please try again.');
        setPhase('error');
        return;
      }

      setPhase('done');
      setTimeout(() => { onDone(); }, 600);
    })();
  }

  if (phase === 'stepup') {
    return (
      <StepUpModal
        actionIntent={kind === 'claim' ? 'support-claim' : 'support-resolve'}
        targetUserId={null}
        onVerified={handleVerified}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ModalShell>
      <h2 className="text-base font-semibold text-ink-high">
        {kind === 'claim' ? 'Claim Support Request' : 'Resolve Support Request'}
      </h2>

      {/* Confirm / submitting */}
      {(phase === 'confirm' || phase === 'submitting') && (
        <div className="space-y-4">
          {kind === 'claim' && (
            <p className="text-sm text-ink-mid">
              Confirm claim of this request?
            </p>
          )}

          {kind === 'resolve' && (
            <>
              <fieldset className="space-y-2">
                <legend className="text-xs font-medium uppercase tracking-wider text-ink-mid">Outcome</legend>
                <label className="flex items-center gap-2 text-sm text-ink-mid">
                  <input
                    type="radio"
                    name="resolve-outcome"
                    value="resolved"
                    checked={outcome === 'resolved'}
                    onChange={() => setOutcome('resolved')}
                    disabled={phase === 'submitting'}
                  />
                  Resolved
                </label>
                <label className="flex items-center gap-2 text-sm text-ink-mid">
                  <input
                    type="radio"
                    name="resolve-outcome"
                    value="rejected"
                    checked={outcome === 'rejected'}
                    onChange={() => setOutcome('rejected')}
                    disabled={phase === 'submitting'}
                  />
                  Rejected
                </label>
              </fieldset>

              <div className="space-y-1">
                <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="resolve-notes">
                  Notes
                </label>
                <textarea
                  id="resolve-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Required"
                  disabled={phase === 'submitting'}
                  className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={phase === 'submitting' || (kind === 'resolve' && !notes.trim())}
              width="full"
            >
              {phase === 'submitting' ? 'Submitting…' : 'Confirm'}
            </Button>
            <button
              type="button"
              onClick={onCancel}
              disabled={phase === 'submitting'}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Done */}
      {phase === 'done' && (
        <p className="text-sm text-[--color-status-ok] text-center py-4">Done.</p>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="space-y-3">
          <ErrorChip>{errorMsg}</ErrorChip>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={() => { setPhase('confirm'); }} width="full">
              Back
            </Button>
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ── SupportInboxPage ──────────────────────────────────────────────────────────

type StatusFilter = 'all' | SupportStatus;

export function SupportInboxPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [modal, setModal] = useState<{ requestId: string; kind: ActionKind } | null>(null);

  const { data, isLoading, isError } = useQuery<SupportListResponse>({
    queryKey: ['support-requests', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await apiFetch<SupportListResponse>(`/admin/support-requests?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load support requests.');
      return res.data;
    },
  });

  function handleDone() {
    setModal(null);
    void queryClient.invalidateQueries({ queryKey: ['support-requests'] });
  }

  const rows = data?.rows ?? [];

  const tabs: { label: string; value: StatusFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Claimed', value: 'claimed' },
    { label: 'Resolved', value: 'resolved' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Inbox"
        description="Player support requests. Step-up required to claim or resolve."
      />

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setStatusFilter(tab.value)}
            className={[
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              statusFilter === tab.value
                ? 'border-primary text-ink-high'
                : 'border-transparent text-ink-low hover:text-ink-mid',
            ].join(' ')}
            data-testid={`tab-${tab.value}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip>Failed to load support requests.</ErrorChip>
      )}

      {/* Empty */}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-low">No support requests found.</p>
      )}

      {/* Table + cards */}
      {!isLoading && !isError && rows.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="support-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Type</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Status</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Email</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Narrative</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Created</th>
                  <th className="px-3 py-2 font-medium text-ink-mid" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.map((row) => (
                  <tr
                    key={row.request_id}
                    className="hover:bg-panel-elev"
                    data-testid={`support-row-${row.request_id}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-ink-low tabular-nums">
                      {row.request_id.slice(0, 8)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.request_type}</td>
                    <td className="px-3 py-2">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="px-3 py-2 text-ink-low text-xs">{row.contact_email}</td>
                    <td className="px-3 py-2 text-ink-mid">
                      {row.narrative.length > 80
                        ? `${row.narrative.slice(0, 80)}…`
                        : row.narrative}
                    </td>
                    <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                      {new Date(row.created_at * 1000).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      {/* Actions for pending/claimed rows.
                          NOTE: For lost-2fa requests we would ideally also show a "Disable 2FA"
                          shortcut via ActionModal, but support requests do not include the target
                          user_id. In v1 we surface "Resolve" only for lost-2fa; the staff member
                          must separately look up the player via /users and disable 2FA from the
                          user profile page. */}
                      <div className="flex gap-2">
                        {row.status === 'pending' && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setModal({ requestId: row.request_id, kind: 'claim' })}
                            data-testid={`claim-btn-${row.request_id}`}
                          >
                            Claim
                          </Button>
                        )}
                        {(row.status === 'pending' || row.status === 'claimed') && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setModal({ requestId: row.request_id, kind: 'resolve' })}
                            data-testid={`resolve-btn-${row.request_id}`}
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden">
            {rows.map((row) => (
              <div
                key={row.request_id}
                className="rounded-sm border border-border bg-panel p-3"
                data-testid={`support-card-${row.request_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="space-y-0.5">
                    <div className="font-mono text-xs font-medium text-ink-high">{row.request_type}</div>
                    <div className="font-mono text-xs text-ink-low">{row.request_id.slice(0, 8)}</div>
                  </div>
                  <StatusPill status={row.status} />
                </div>
                <div className="space-y-0.5 text-xs text-ink-low mb-2">
                  <div>{row.contact_email}</div>
                  <div className="text-ink-mid line-clamp-2">
                    {row.narrative.length > 80
                      ? `${row.narrative.slice(0, 80)}…`
                      : row.narrative}
                  </div>
                  <div className="font-mono tabular-nums">{new Date(row.created_at * 1000).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  {row.status === 'pending' && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setModal({ requestId: row.request_id, kind: 'claim' })}
                      data-testid={`claim-btn-${row.request_id}`}
                    >
                      Claim
                    </Button>
                  )}
                  {(row.status === 'pending' || row.status === 'claimed') && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setModal({ requestId: row.request_id, kind: 'resolve' })}
                      data-testid={`resolve-btn-${row.request_id}`}
                    >
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal */}
      {modal && (
        <SupportActionModal
          requestId={modal.requestId}
          kind={modal.kind}
          onDone={handleDone}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
